import api from './api'
import type {
  FilterState,
  SavedView,
  ColumnId,
  ReportVersion,
  ViewAclUpdate,
  ViewSchedule,
  ViewScheduleInput,
} from '@/pages/pipeline-intelligence/types'
import { normalizeViewStatus } from '@/pages/pipeline-intelligence/helpers'
import { toCamelCase } from '@/utils/transform'

type ApiView = {
  id: string
  name: string
  description: string
  status: string
  ownerEmail: string
  version: number
  updatedAt: string | null
  columns: string[]
  filters: FilterState
  isSystem?: boolean
  isShared?: boolean
  editors?: string[]
  viewers?: string[]
  versions?: Array<{
    id?: string
    version: number
    authorEmail: string
    note: string
    columns: string[]
    filters: FilterState
    createdAt: string | null
  }>
}

function mapVersion(row: NonNullable<ApiView['versions']>[number]): ReportVersion {
  return {
    id: row.id,
    version: row.version,
    createdAt: row.createdAt,
    authorEmail: row.authorEmail,
    note: row.note,
    columns: row.columns as ColumnId[],
    filters: row.filters,
  }
}

function mapView(row: ApiView): SavedView {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? '',
    status: normalizeViewStatus(row.status),
    ownerEmail: row.ownerEmail,
    version: row.version,
    updatedAt: row.updatedAt,
    columns: (row.columns ?? []) as ColumnId[],
    filters: row.filters,
    versions: (row.versions ?? []).map(mapVersion),
    isSystem: row.isSystem,
    isShared: row.isShared,
    editors: row.editors ?? [],
    viewers: row.viewers ?? [],
  }
}

function mapSchedules(data: unknown): ViewSchedule[] {
  const normalized = toCamelCase<ViewSchedule[] | { schedules?: ViewSchedule[] }>(data)
  const rows = Array.isArray(normalized) ? normalized : (normalized.schedules ?? [])
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    template: row.template ?? 'pipeline_summary',
    frequency: row.frequency,
    dayOfWeek: row.dayOfWeek ?? null,
    dayOfMonth: row.dayOfMonth ?? null,
    hourUtc: row.hourUtc,
    minuteLocal: row.minuteLocal ?? 0,
    willEmail: row.willEmail,
    deliveryBlockers: row.deliveryBlockers ?? [],
    timezone: row.timezone ?? 'UTC',
    businessDaysOnly: Boolean(row.businessDaysOnly),
    triggerType: row.triggerType ?? 'time',
    triggerEventType: row.triggerEventType ?? null,
    recipients: Array.isArray(row.recipients) ? row.recipients : [],
    format: row.format,
    deliveryMode: row.deliveryMode ?? 'none',
    isActive: Boolean(row.isActive),
    pinVersion: row.pinVersion ?? null,
    lastRunAt: row.lastRunAt ?? null,
    lastRunStatus: row.lastRunStatus ?? null,
    nextRunAt: row.nextRunAt ?? null,
    createdAt: row.createdAt ?? null,
    updatedAt: row.updatedAt ?? null,
  }))
}

export const pipelineViewsService = {
  list: () =>
    api.get<ApiView[]>('/pipeline-views/').then((r) => r.data.map(mapView)),

  get: (id: string) =>
    api.get<ApiView>(`/pipeline-views/${id}`).then((r) => mapView(r.data)),

  create: (body: {
    name: string
    description?: string
    columns: ColumnId[]
    filters: FilterState
    note?: string
    is_shared?: boolean
  }) =>
    api.post<ApiView>('/pipeline-views/', body).then((r) => mapView(r.data)),

  update: (
    id: string,
    body: {
      name?: string
      description?: string
      is_shared?: boolean
      status?: string
      /** Persist the working default column order (register G3). */
      columns?: ColumnId[]
    },
  ) => api.put<ApiView>(`/pipeline-views/${id}`, body).then((r) => mapView(r.data)),

  saveVersion: (
    id: string,
    body: { note: string; columns: ColumnId[]; filters: FilterState },
  ) =>
    api.post<ApiView>(`/pipeline-views/${id}/versions`, body).then((r) => mapView(r.data)),

  restoreVersion: (id: string, version: number, body?: { note?: string }) =>
    api
      .post<ApiView>(`/pipeline-views/${id}/versions/${version}/restore`, body ?? {})
      .then((r) => mapView(r.data)),

  branch: (id: string, body: { name: string; description?: string }) =>
    api.post<ApiView>(`/pipeline-views/${id}/branch`, body).then((r) => mapView(r.data)),

  publish: (id: string) =>
    api.post<ApiView>(`/pipeline-views/${id}/publish`).then((r) => mapView(r.data)),

  updateAcl: (id: string, body: ViewAclUpdate) =>
    api.put<ApiView>(`/pipeline-views/${id}/acl`, body).then((r) => mapView(r.data)),

  listSchedules: (id: string) =>
    api.get(`/pipeline-views/${id}/schedules`).then((r) => mapSchedules(r.data)),

  createSchedule: (id: string, body: ViewScheduleInput) =>
    api
      .post(`/pipeline-views/${id}/schedules`, body)
      .then((r) => mapSchedules([r.data])[0]),

  updateSchedule: (id: string, scheduleId: string, body: Partial<ViewScheduleInput>) =>
    api
      .put(`/pipeline-views/${id}/schedules/${scheduleId}`, body)
      .then((r) => mapSchedules([r.data])[0]),

  activateSchedule: (id: string, scheduleId: string) =>
    api
      .post(`/pipeline-views/${id}/schedules/${scheduleId}/activate`)
      .then((r) => mapSchedules([r.data])[0]),

  deleteSchedule: (id: string, scheduleId: string) =>
    api.delete(`/pipeline-views/${id}/schedules/${scheduleId}`).then(() => undefined),

  runScheduleNow: (id: string, scheduleId: string) =>
    api
      .post(`/pipeline-views/${id}/schedules/${scheduleId}/run-now`)
      .then((r) => toCamelCase<Record<string, unknown>>(r.data)),
}
